import { Component } from '@theme/component';

/**
 * Custom element that manages video playback controls for hero section videos.
 * Handles play/pause and mute/unmute functionality for video_1 and video_2 elements.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} playPauseButton
 * @property {HTMLButtonElement} muteButton
 * @property {HTMLVideoElement[]} videos
 *
 * @extends {Component<Refs>}
 */
export class HeroVideoControls extends Component {
  /**
   * Current play state
   * @type {boolean}
   */
  #isPlaying = true;

  /**
   * Current mute state
   * @type {boolean}
   */
  #isMuted = true;

  /**
   * Array of video elements to control
   * @type {HTMLVideoElement[]}
   */
  #videos = [];

  /**
   * Bound event handlers for video elements
   * @type {Map<HTMLVideoElement, {play: (event: Event) => void, pause: (event: Event) => void, volumechange: (event: Event) => void}>}
   */
  #videoHandlers = new Map();

  /**
   * Bound event handler for play/pause button
   * @type {((event: MouseEvent) => void)|null}
   */
  #playPauseHandler = null;

  /**
   * Bound event handler for mute button
   * @type {((event: MouseEvent) => void)|null}
   */
  #muteHandler = null;

  /**
   * Bound event handler for component click
   * @type {((event: MouseEvent) => void)|null}
   */
  #componentClickHandler = null;

  connectedCallback() {
    super.connectedCallback();

    // Find all video elements within the hero section
    const heroSection = this.closest('.hero');
    if (heroSection) {
      this.#videos = Array.from(heroSection.querySelectorAll('.hero__video'));
    }

    // Initialize state from first video
    if (this.#videos.length > 0 && this.#videos[0]) {
      const firstVideo = this.#videos[0];
      this.#isPlaying = !firstVideo.paused;
      this.#isMuted = firstVideo.muted;
      this.#updateButtonIcons();
    }

    // Listen to video events to keep state in sync
    this.#videos.forEach(video => {
      const playHandler = () => {
        this.#isPlaying = true;
        this.#updateButtonIcons();
      };
      const pauseHandler = () => {
        this.#isPlaying = false;
        this.#updateButtonIcons();
      };
      const volumechangeHandler = () => {
        this.#isMuted = video.muted;
        this.#updateButtonIcons();
      };

      video.addEventListener('play', playHandler);
      video.addEventListener('pause', pauseHandler);
      video.addEventListener('volumechange', volumechangeHandler);

      // Store handlers for cleanup
      this.#videoHandlers.set(video, {
        play: playHandler,
        pause: pauseHandler,
        volumechange: volumechangeHandler
      });
    });

    // Setup button event listeners
    if (this.refs.playPauseButton) {
      this.#playPauseHandler = (e) => {
        e.stopPropagation();
        e.preventDefault();
        this.playPause();
      };
      this.refs.playPauseButton.addEventListener('click', this.#playPauseHandler);
    }

    if (this.refs.muteButton) {
      this.#muteHandler = (e) => {
        e.stopPropagation();
        e.preventDefault();
        this.toggleMute();
      };
      this.refs.muteButton.addEventListener('click', this.#muteHandler);
    }

    // Stop propagation on the controls container itself
    this.#componentClickHandler = (e) => {
      e.stopPropagation();
    };
    this.addEventListener('click', this.#componentClickHandler);
  }

  /**
   * Cleanup when component is disconnected from DOM
   */
  disconnectedCallback() {
    // Remove video event listeners
    this.#videoHandlers.forEach((handlers, video) => {
      video.removeEventListener('play', handlers.play);
      video.removeEventListener('pause', handlers.pause);
      video.removeEventListener('volumechange', handlers.volumechange);
    });
    this.#videoHandlers.clear();

    // Remove button event listeners
    if (this.refs.playPauseButton && this.#playPauseHandler) {
      this.refs.playPauseButton.removeEventListener('click', this.#playPauseHandler);
      this.#playPauseHandler = null;
    }

    if (this.refs.muteButton && this.#muteHandler) {
      this.refs.muteButton.removeEventListener('click', this.#muteHandler);
      this.#muteHandler = null;
    }

    // Remove component click listener
    if (this.#componentClickHandler) {
      this.removeEventListener('click', this.#componentClickHandler);
      this.#componentClickHandler = null;
    }

    // Clear videos array
    this.#videos = [];

    super.disconnectedCallback?.();
  }

  /**
   * Toggles play/pause state for all videos
   */
  playPause() {
    if (this.#videos.length === 0) return;

    if (this.#isPlaying) {
      // Pause is synchronous, no Promise to handle
      this.#videos.forEach(video => video.pause());
      // State will be updated by 'pause' event handlers
    } else {
      // Play returns a Promise that can reject
      // Handle rejections to avoid unhandled promise rejections
      // Event handlers will automatically sync state when videos actually play
      this.#videos.forEach(video => {
        video.play().catch(error => {
          // Handle autoplay policy or other play failures silently
          // The 'play' event won't fire if play() rejects, so state remains paused
          // This is the correct behavior - if play fails, video stays paused
        });
      });
      // State will be updated by 'play' event handlers when videos actually start playing
    }
  }

  /**
   * Toggles mute/unmute state for all videos
   */
  toggleMute() {
    if (this.#videos.length === 0) return;

    // Toggle mute state on all videos
    // The 'volumechange' event handlers will automatically sync state and update UI
    const newMutedState = !this.#isMuted;
    this.#videos.forEach(video => {
      video.muted = newMutedState;
    });
    // State will be updated by 'volumechange' event handlers
  }

  /**
   * Updates button icons based on current state
   */
  #updateButtonIcons() {
    if (!this.refs.playPauseButton || !this.refs.muteButton) return;

    // Update play/pause icon
    const playPauseIcon = this.refs.playPauseButton.querySelector('.hero-video-controls__icon');
    if (playPauseIcon) {
      playPauseIcon.innerHTML = this.#isPlaying
        ? this.#getPauseIcon()
        : this.#getPlayIcon();
    }

    // Update mute/unmute icon
    const muteIcon = this.refs.muteButton.querySelector('.hero-video-controls__icon');
    if (muteIcon) {
      muteIcon.innerHTML = this.#isMuted
        ? this.#getMuteIcon()
        : this.#getUnmuteIcon();
    }

    // Update aria labels
    this.refs.playPauseButton.setAttribute(
      'aria-label',
      this.#isPlaying
        ? this.refs.playPauseButton.getAttribute('data-label-pause') || 'Pause video'
        : this.refs.playPauseButton.getAttribute('data-label-play') || 'Play video'
    );

    this.refs.muteButton.setAttribute(
      'aria-label',
      this.#isMuted
        ? this.refs.muteButton.getAttribute('data-label-unmute') || 'Unmute video'
        : this.refs.muteButton.getAttribute('data-label-mute') || 'Mute video'
    );
  }

  /**
   * Returns play icon SVG
   */
  #getPlayIcon() {
    return '<svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M15.9167 15.9106C15.9167 15.3441 15.9167 15.0608 16.0349 14.9047C16.1378 14.7687 16.2951 14.6845 16.4653 14.6743C16.6608 14.6626 16.8964 14.8198 17.3678 15.134L23.5019 19.2234C23.8914 19.483 24.0861 19.6129 24.154 19.7765C24.2133 19.9196 24.2133 20.0804 24.154 20.2234C24.0861 20.3871 23.8914 20.5169 23.5019 20.7766L17.3678 24.8659C16.8964 25.1802 16.6608 25.3373 16.4653 25.3256C16.2951 25.3155 16.1378 25.2313 16.0349 25.0953C15.9167 24.9391 15.9167 24.6559 15.9167 24.0894V15.9106Z" stroke="white" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  }

  /**
   * Returns pause icon SVG
   */
  #getPauseIcon() {
    return '<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40" fill="none"><g clip-path="url(#clip0_36064_607)"><path d="M16.5 15H18.5V25H16.5V15ZM21.5 15H23.5V25H21.5V15Z" fill="white"/></g><defs><clipPath id="clip0_36064_607"><rect width="7" height="10" fill="white" transform="translate(16.5 15)"/></clipPath></defs></svg>';
  }

  /**
   * Returns mute icon SVG
   */
  #getMuteIcon() {
    return '<svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg"><g clip-path="url(#clip0_36017_12600)"><path d="M9.0249 15.9313V24.2H14.2749L19.5249 28.4V11.8625L14.2749 16.0625H9.0249V15.9313ZM14.9312 16.7188L18.4749 13.9625V26.3L14.9312 23.5438L14.6687 23.2813H10.0749V17.1125H14.5374L14.7999 16.85L14.9312 16.7188Z" fill="white"/><path d="M29.3687 17.2438L28.5812 16.4563L25.8249 19.3438L22.9374 16.4563L22.1499 17.2438L25.0374 20.1313L22.2812 22.8875L22.1499 23.0188L22.9374 23.675L25.8249 20.9188L28.5812 23.675L29.3687 23.0188L26.4812 20.1313L29.3687 17.2438Z" fill="white"/></g><defs><clipPath id="clip0_36017_12600"><rect width="21" height="21" fill="white" transform="translate(8.5 9.5)"/></clipPath></defs></svg>';
  }

  /**
   * Returns unmute icon SVG
   */
  #getUnmuteIcon() {
    return '<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40" fill="none"><path d="M24 28L19 24H14V16H19L24 12V28ZM19.625 16.7832L19.3496 17.002H15V23.002H19.3506L19.624 23.2207H19.625L23 25.9199V14.083L19.625 16.7832Z" fill="white"/></svg>';
  }
}

customElements.define('hero-video-controls', HeroVideoControls);

